import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CommonModule } from '@angular/common';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { Router, RouterModule } from '@angular/router';
import { DocumentSpecialMeasuresFormComponent } from '@/pages/documents/document-special-measures-form/document-special-measures-form.component';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Utils } from '@/utils/utils';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { ClientFormComponent } from '@/pages/clients/client-form/client-form.component';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';
import { ChangeCalculatorComponent } from '@/components/change-calculator/change-calculator.component';

const TABLE_STATUSES: Record<string, { label: string; classes: string }> = {
  free: { label: 'Libre', classes: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-400' },
  occupied: { label: 'Ocupada', classes: 'bg-red-50 border-red-200 text-red-700' },
  reserved: { label: 'Reservada', classes: 'bg-amber-50 border-amber-200 text-amber-700' },
  dirty: { label: 'Por limpiar', classes: 'bg-slate-100 border-slate-300 text-slate-500' },
};

/** Colores por familia para los botones de la carta, puramente decorativos (categories no tiene campo color). */
const CATEGORY_PALETTE = [
  { tile: 'bg-emerald-50 border-emerald-200 text-emerald-900', active: 'bg-emerald-600 border-emerald-600 text-white', dot: 'bg-emerald-500' },
  { tile: 'bg-blue-50 border-blue-200 text-blue-900', active: 'bg-blue-600 border-blue-600 text-white', dot: 'bg-blue-500' },
  { tile: 'bg-amber-50 border-amber-200 text-amber-900', active: 'bg-amber-500 border-amber-500 text-white', dot: 'bg-amber-500' },
  { tile: 'bg-rose-50 border-rose-200 text-rose-900', active: 'bg-rose-600 border-rose-600 text-white', dot: 'bg-rose-500' },
  { tile: 'bg-violet-50 border-violet-200 text-violet-900', active: 'bg-violet-600 border-violet-600 text-white', dot: 'bg-violet-500' },
  { tile: 'bg-cyan-50 border-cyan-200 text-cyan-900', active: 'bg-cyan-600 border-cyan-600 text-white', dot: 'bg-cyan-500' },
  { tile: 'bg-lime-50 border-lime-200 text-lime-900', active: 'bg-lime-600 border-lime-600 text-white', dot: 'bg-lime-500' },
  { tile: 'bg-orange-50 border-orange-200 text-orange-900', active: 'bg-orange-500 border-orange-500 text-white', dot: 'bg-orange-500' },
];
const NO_CATEGORY_COLORS = { tile: 'bg-slate-50 border-slate-200 text-slate-800', active: 'bg-slate-700 border-slate-700 text-white', dot: 'bg-slate-400' };

@Component({
  imports: [
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CustomSelectComponent,
    RouterModule,
    DocumentSpecialMeasuresFormComponent,
    ChangeCalculatorComponent,
  ],
  templateUrl: './restaurant-ticket-form.page.html'
})
export class RestaurantTicketFormPage implements OnInit {
  public loading = false;
  public loadingTables = false;
  public saving = false;

  /** 'tables' shows the floor grid, 'order' shows the product/order entry form for `selectedTable`. */
  public view: 'tables' | 'order' = 'tables';

  public tables: any[] = [];
  public selectedTable: any = null;
  public tableStatuses = TABLE_STATUSES;

  /** Set when the order view is showing an already-occupied table's in-progress ticket rather than a brand new one. */
  public editingTicketId: number | null = null;

  /** Se elige al sacar el ticket: mientras la mesa está abierta el pedido es un borrador sin método de pago. */
  public paymentMethod: string | null = null;
  public showChangeCalculator = false;
  public paymentMethods = [
    { id: 'efectivo', name: 'Efectivo' },
    { id: 'tarjeta', name: 'Tarjeta' },
    { id: 'bizum', name: 'Bizum' },
  ];

  public saveForm!: FormGroup;
  public idEditMeasures = 0;
  public editMeasures = false;
  public specialMeasures: { w: number, h: number, t: number } = { w: 1, h: 1, t: 1 };

  // ── Carta tipo PDA ──
  /** 'all' = todas las familias, null = productos sin familia. */
  public activeCategoryId: number | null | 'all' = 'all';
  public searchQuery = '';
  /** En móvil solo cabe un panel (la carta o la comanda); en md+ se ven los dos a la vez. */
  public mobilePanel: 'menu' | 'order' = 'menu';
  /** Línea de la comanda desplegada para editar precio/descuento/medidas. */
  public expandedLine: number | null = null;

  public dropdowns: any = {
    clients: [],
    products: [],
    categories: [],
    tariffs: [],
    offices: [],
    tax_config: [],
  };

  public clientFormComponent = ClientFormComponent;

  // el ticket siempre se calcula contra la tarifa de ticket de la compañía
  public ticketTariffId: number | null = null;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.getDropdowns();
    this.getTables();
  }

  createForm() {
    this.saveForm = new FormGroup({
      year: new FormControl(new Date().getFullYear(), [Validators.required]),
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      table_id: new FormControl(null, [Validators.required]),
      tax_included: new FormControl(1),
      user_id: new FormControl(null),
      discount_absolute_bool: new FormControl(0),
      discount: new FormControl(0),
      notes: new FormControl(null),
      client_id: new FormControl(null),
      subtotal: new FormControl('0.00'),
      total: new FormControl('0.00'),
      items: new FormArray([], [Validators.required]),
      office_id: new FormControl(null),
    });

    this.saveForm.valueChanges.subscribe(() => this.calculateTicket());
  }

  get items() { return this.saveForm.get('items') as FormArray; }
  get itemControls() { return this.items.controls; }

  get isTaxIncluded(): boolean {
    return Number(this.saveForm.get('tax_included')?.value) === 1;
  }

  // ── Mesas ─────────────────────────────────────────────────────────────

  getTables() {
    this.loadingTables = true;
    this.apiService.getPetition(Urls.urls.tables).subscribe({
      next: (result: any) => {
        this.tables = (result.list || []).sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.loadingTables = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingTables = false;
        this.cdr.detectChanges();
      }
    });
  }

  tableStatus(status: string) {
    return this.tableStatuses[status] || this.tableStatuses['free'];
  }

  selectTable(table: any) {
    if (table.status === 'occupied') {
      this.openExistingOrder(table);
      return;
    }

    if (table.status !== 'free') {
      Utils.notification.alert('Esta mesa no está libre');
      return;
    }

    this.resetOrderForm();
    this.selectedTable = table;
    this.saveForm.get('table_id')?.setValue(table.id);
    this.view = 'order';
  }

  /** La mesa ya tiene un pedido en curso: lo cargamos en la misma vista de pedido para poder modificarlo. */
  openExistingOrder(table: any) {
    this.loadingTables = true;
    this.apiService.getPetition(Urls.urls.tickets, `?table_id=${table.id}&limit=1`).subscribe({
      next: (result: any) => {
        const ticket = (result.list || [])[0];

        if (!ticket) {
          this.loadingTables = false;
          Utils.notification.alert('No se ha encontrado el pedido de esta mesa');
          this.cdr.detectChanges();
          return;
        }

        this.loadExistingTicket(ticket.id, table);
      },
      error: () => {
        this.loadingTables = false;
        Utils.notification.alert('Error al buscar el pedido de la mesa');
        this.cdr.detectChanges();
      }
    });
  }

  private loadExistingTicket(ticketId: number, table: any) {
    this.apiService.getById(Urls.urls.tickets, ticketId).subscribe({
      next: (data: any) => {
        const ticket = data.ticket || {};

        this.resetOrderForm();
        this.editingTicketId = ticket.id;
        this.selectedTable = table;

        this.saveForm.patchValue({
          year: ticket.year ?? new Date().getFullYear(),
          date: ticket.date ? ticket.date.split(' ')[0] : new Date().toISOString().split('T')[0],
          table_id: ticket.table_id ?? table.id,
          tax_included: ticket.tax_included ?? 1,
          user_id: ticket.user_id ?? this.authenticationService.currentUserValue.id,
          discount_absolute_bool: ticket.discount_absolute_bool ?? 0,
          discount: ticket.discount ?? 0,
          notes: ticket.notes ?? null,
          client_id: ticket.client_id ?? null,
          office_id: ticket.office_id ?? null,
        });

        const details = data.ticket_details || [];
        details.forEach((d: any) => {
          const pivot = d.pivot || {};
          this.addItem({
            id: pivot.id || null,
            product_id: d.id ?? null,
            name: pivot.personalized_desc || d.name || 'Nuevo artículo',
            quantity: pivot.quantity ?? 1,
            discount: pivot.discount ?? 0,
            price: (pivot.price !== undefined && pivot.price !== null) ? Number(pivot.price) / 100 : 0,
            special_measures: d.special_measures ?? null,
            tax: pivot.tax ?? this.getProductTaxQuotient(d),
            apply_tax: pivot.apply_tax ? 1 : 0,
          });
        });

        this.view = 'order';
        this.loadingTables = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingTables = false;
        Utils.notification.alert('Error al cargar el pedido de la mesa');
        this.cdr.detectChanges();
      }
    });
  }

  /** Deja el formulario listo para una mesa nueva, sin arrastrar artículos/datos de un pedido anterior. */
  private resetOrderForm() {
    this.editingTicketId = null;
    this.paymentMethod = null;
    this.activeCategoryId = 'all';
    this.searchQuery = '';
    this.mobilePanel = 'menu';
    this.expandedLine = null;

    this.saveForm.patchValue({
      table_id: null,
      tax_included: 1,
      discount_absolute_bool: 0,
      discount: 0,
      notes: null,
      client_id: null,
      subtotal: '0.00',
      total: '0.00',
    }, { emitEvent: false });

    while (this.items.length) {
      this.items.removeAt(0);
    }
  }

  backToTables() {
    this.view = 'tables';
    this.selectedTable = null;
    this.resetOrderForm();
    this.getTables();
  }

  // ── Precios / tarifa ─────────────────────────────────────────────────

  getPrice(product: any, tariffId: any = null): number {
    const choosedTariffId = tariffId ?? this.ticketTariffId;
    const tariff = this.dropdowns.tariffs?.find((t: any) => t.id == choosedTariffId);

    if (!tariff || tariff.tax) {
      return (product.price_ticket || 0) / 100;
    }

    try {
      const more_prices = typeof product.more_prices === 'string'
        ? JSON.parse(product.more_prices)
        : product.more_prices;

      const specificPrice = more_prices?.find((p: any) => p.id == choosedTariffId);
      return (specificPrice ? specificPrice.price : (product.price_ticket || 0)) / 100;
    } catch (e) {
      return (product.price_ticket || 0) / 100;
    }
  }

  getDropdowns() {
    const config = { clients: {}, products: {}, categories: {}, companies: {}, offices: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];

        const ticketTariff = company.ticket_tariff ? [{ id: company.ticket_tariff, name: `Base (${company.ticket_tariff})`, tax: true }] : [];

        const invoice_tariffs = JSON.parse(company.invoice_tariffs || '[]').map((tariff: any) => ({
          id: Number(tariff.id),
          name: `Tarifa ${tariff.id} (${tariff.quotient})`,
          tax: false
        }));

        this.dropdowns.tariffs = [
          ...ticketTariff,
          ...invoice_tariffs
        ];

        this.ticketTariffId = company.ticket_tariff ?? null;

        this.dropdowns.tax_config = company.tax_config ? JSON.parse(company.tax_config) : [];

        const specialClientIds = company.special_clients
          ? (typeof company.special_clients === 'string' ? JSON.parse(company.special_clients) : company.special_clients)
          : [];

        this.dropdowns.clients = (data.clients || []).filter((c: any) => specialClientIds.some((id: any) => id == c.id));
        this.dropdowns.products = data.products || [];
        this.dropdowns.categories = (data.categories || []).sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.dropdowns.offices = data.offices || [];

        const currentUserId = this.authenticationService.currentUserValue.id;
        this.saveForm.get('user_id')?.setValue(currentUserId);
        this.saveForm.get('office_id')?.setValue(this.dropdowns.offices[0]?.id ?? null);

        this.cdr.detectChanges();
      }
    });
  }

  // ── Carta ─────────────────────────────────────────────────────────────

  /** Solo las familias que tienen algún producto, para no mostrar pestañas vacías. */
  get menuCategories(): any[] {
    const usedIds = new Set(this.dropdowns.products.map((p: any) => p.category_id ?? null));
    return this.dropdowns.categories.filter((c: any) => usedIds.has(c.id));
  }

  /** Con texto de búsqueda se busca en toda la carta, ignorando la familia seleccionada. */
  get menuProducts(): any[] {
    const q = this.searchQuery.trim().toLowerCase();

    return this.dropdowns.products
      .filter((p: any) => {
        if (q) {
          return p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q);
        }
        return this.activeCategoryId === 'all' || (p.category_id ?? null) === this.activeCategoryId;
      })
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  categoryColors(categoryId: number | null) {
    const index = this.dropdowns.categories.findIndex((c: any) => c.id === categoryId);
    return index === -1 ? NO_CATEGORY_COLORS : CATEGORY_PALETTE[index % CATEGORY_PALETTE.length];
  }

  selectCategory(id: number | null | 'all', searchInput: HTMLInputElement) {
    this.activeCategoryId = id;
    this.clearSearch(searchInput);
  }

  clearSearch(searchInput: HTMLInputElement) {
    searchInput.value = '';
    this.searchQuery = '';
  }

  /** Enter en el buscador añade el primer resultado. */
  addFirstProduct(searchInput: HTMLInputElement) {
    const first = this.menuProducts[0];
    if (!first) return;

    this.tapProduct(first);
    this.clearSearch(searchInput);
  }

  /** Unidades de un producto ya en la comanda, para el contador del botón de la carta. */
  orderedQuantity(productId: any): number {
    return this.items.controls
      .filter(c => c.get('product_id')?.value == productId)
      .reduce((total, c) => total + Number(c.get('quantity')?.value || 0), 0);
  }

  get orderedUnits(): number {
    return this.items.controls.reduce((total, c) => total + Number(c.get('quantity')?.value || 0), 0);
  }

  /**
   * Pulsar un producto de la carta: si ya hay una línea "limpia" de ese producto (sin medidas
   * especiales, sin descuento y con su precio de carta) suma una unidad, como en una PDA; si no, añade una línea nueva.
   */
  tapProduct(product: any) {
    const price = this.getPrice(product);

    const existing = product.special_measures ? null : this.items.controls.find(c =>
      c.get('product_id')?.value == product.id &&
      !Number(c.get('discount')?.value) &&
      Number(c.get('price')?.value) === price &&
      c.get('name')?.value === product.name
    );

    if (existing) {
      existing.get('quantity')?.setValue(Number(existing.get('quantity')?.value || 0) + 1);
      return;
    }

    this.addItem({
      id: null,
      product_id: product.id,
      name: product.name,
      price,
      quantity: 1,
      discount: 0,
      special_measures: [],
      tax: this.getProductTaxQuotient(product),
      apply_tax: 1,
    });
  }

  incrementLine(index: number) {
    const control = this.items.at(index).get('quantity');
    control?.setValue(Number(control.value || 0) + 1);
  }

  /** Restar la última unidad elimina la línea. */
  decrementLine(index: number) {
    const control = this.items.at(index).get('quantity');
    const quantity = Number(control?.value || 0);

    if (quantity <= 1) {
      this.removeItem(index);
      return;
    }

    control?.setValue(quantity - 1);
  }

  toggleLine(index: number) {
    this.expandedLine = this.expandedLine === index ? null : index;
  }

  addItem(itemData: any = null) {
    const itemGroup = new FormGroup({
      id: new FormControl(itemData?.id || null),
      product_id: new FormControl(itemData?.product_id || null, [Validators.required]),
      name: new FormControl(itemData?.name || 'Nuevo artículo'),
      quantity: new FormControl(itemData?.quantity || 1, [Validators.required, Validators.min(1)]),
      discount: new FormControl(itemData?.discount || 0),
      price: new FormControl(itemData?.price || 0),
      special_measures: new FormControl(itemData?.special_measures || null),
      tax: new FormControl(itemData?.tax ?? null),
      apply_tax: new FormControl(itemData?.apply_tax ?? 1),
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.expandedLine = null;
    this.calculateTicket();
  }

  toggleEditMeasures(specialMeasuresObject: any = '', specialMeasures: string = '') {
    if (specialMeasures) {
      if (typeof specialMeasures == 'string')
        this.specialMeasures = JSON.parse(specialMeasures);
    }
    this.idEditMeasures = 0;
    this.editMeasures = !this.editMeasures;
  }

  openMeasures(index: number, currentSavedMeasures: any, productConfig: string) {
    this.idEditMeasures = index;

    this.specialMeasures = (currentSavedMeasures && Array.isArray(currentSavedMeasures) && currentSavedMeasures.length > 0)
      ? currentSavedMeasures
      : JSON.parse(productConfig);

    this.editMeasures = true;
  }

  acceptMeasures(item: { totalVolume: string, details: any }) {
    const product = this.items.controls[this.idEditMeasures];
    product.get('special_measures')?.setValue(item.details);
    product.get('quantity')?.setValue(item.totalVolume);

    this.toggleEditMeasures();
    this.cdr.detectChanges();
  }

  getItemQuotient(item: any): number {
    const product_id = item.get('product_id')?.value;
    const product = this.dropdowns.products.find((p: any) => p.id == product_id);
    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product?.tax_type);
    return taxConfig && taxConfig.quotient ? Number(taxConfig.quotient) : 21;
  }

  getAndCalculateWithOutTax(item: any) {
    const quantity = item.get('quantity')?.value || 0;
    const price = item.get('price')?.value || 0;
    const discount = (100 - (item.get('discount')?.value || 0)) / 100;

    const lineTotal = quantity * price * discount;

    if (this.isTaxIncluded) {
      const quotient = this.getItemQuotient(item);
      return (lineTotal / ((quotient + 100) / 100)).toFixed(2);
    }

    return lineTotal.toFixed(2);
  }

  getAndCalculateWithTax(item: any) {
    const quantity = item.get('quantity')?.value || 0;
    const price = item.get('price')?.value || 0;
    const discount = (100 - (item.get('discount')?.value || 0)) / 100;

    const lineTotal = quantity * price * discount;

    if (this.isTaxIncluded) {
      return lineTotal.toFixed(2);
    }

    const quotient = this.getItemQuotient(item);
    return (lineTotal * ((quotient + 100) / 100)).toFixed(2);
  }

  calculateTicket() {
    let sub = 0;
    let tot = 0;

    this.items.controls.forEach(c => {
      sub += Number(this.getAndCalculateWithOutTax(c));
      tot += Number(this.getAndCalculateWithTax(c));
    });

    const disc = this.saveForm.get('discount')?.value || 0;
    const isAbsolute = Number(this.saveForm.get('discount_absolute_bool')?.value) === 1;

    const lastSub = sub;

    if (isAbsolute) {
      sub -= disc;
    } else {
      sub *= (1 - (disc / 100));
    }

    tot = lastSub > 0 ? sub * tot / lastSub : 0;

    if (isNaN(tot)) {
      tot = 0;
    }

    this.saveForm.get('subtotal')?.setValue(sub.toFixed(2), { emitEvent: false });
    this.saveForm.get('total')?.setValue(tot.toFixed(2), { emitEvent: false });
  }

  // Keeps a line renamed from ticket-form.page.ts: the backend recreates every line on update,
  // so an override not re-sent from here would be lost.
  private personalizedDesc(item: any): string | null {
    const originalName = (this.getProductInfo(item.product_id)?.name || '').trim();
    const currentName = (item.name || '').trim();
    return (currentName && currentName !== originalName) ? currentName : null;
  }

  getProductInfo(productId: any) {
    if (!productId || !this.dropdowns.products) return null;
    return this.dropdowns.products.find((p: any) => p.id == productId);
  }

  getProductTaxQuotient(product: any): number | null {
    if (!product || !this.dropdowns.tax_config) return null;
    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product.tax_type);
    return taxConfig?.quotient ? Number(taxConfig.quotient) : null;
  }

  // ── Guardado ──────────────────────────────────────────────────────────

  /** La mesa abierta se guarda como borrador (no mueve stock ni cuenta en caja); al sacar el ticket pasa a final con su método de pago. */
  private buildPayload(paymentMethod: string | null = null) {
    const payload = {
      ...this.saveForm.value,
      status: paymentMethod ? null : 'draft',
      payment_method: paymentMethod,
    };

    payload.subtotal = Math.abs(payload.subtotal * 100);
    payload.total = Math.abs(payload.total * 100);

    payload.items = payload.items.map((item: any) => ({
      ...item,
      price: Math.abs(item.price * 100),
      special_measures: item.special_measures || null,
      personalized_desc: this.personalizedDesc(item),
    }));

    return payload;
  }

  sendOrder() {
    if (!this.saveForm.valid) {
      Utils.notification.alert('Selecciona una mesa y añade al menos un artículo');
      return;
    }

    this.saving = true;
    const payload = this.buildPayload();

    this.apiService.save(Urls.urls.tickets, payload, this.editingTicketId || 0).subscribe({
      next: () => {
        Utils.notification.success(this.editingTicketId
          ? 'Pedido actualizado en mesa ' + this.selectedTable.name
          : 'Pedido enviado a mesa ' + this.selectedTable.name);

        if (this.editingTicketId) {
          this.finishOrder();
        } else {
          this.markTableOccupied();
        }
      },
      error: (error: any) => {
        this.saving = false;
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al enviar el pedido');
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Paso opcional antes de checkoutOrder(): en efectivo abre la calculadora de cambio, que llama a
   * checkoutOrder() al confirmar. Para sacar el ticket sin calculadora, el botón llama a checkoutOrder() directamente.
   */
  finalizeCheckout() {
    if (this.paymentMethod === 'efectivo') {
      this.showChangeCalculator = true;
      return;
    }

    this.checkoutOrder();
  }

  /** Guarda los cambios, libera la mesa y abre el ticket para imprimir - cierra el pedido de la mesa. */
  checkoutOrder() {
    if (!this.saveForm.valid) {
      Utils.notification.alert('Añade al menos un artículo antes de sacar el ticket');
      return;
    }

    if (!this.paymentMethod) {
      Utils.notification.alert('Selecciona el método de pago antes de sacar el ticket');
      return;
    }

    this.showChangeCalculator = false;
    this.saving = true;
    const payload = this.buildPayload(this.paymentMethod);

    this.apiService.save(Urls.urls.tickets, payload, this.editingTicketId || 0).subscribe({
      next: (entity: any) => this.releaseTableAndPrint(entity.ticket.id),
      error: (error: any) => {
        this.saving = false;
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al sacar el ticket');
        this.cdr.detectChanges();
      }
    });
  }

  /** Best-effort: el pedido ya se ha guardado, así que un fallo aquí no debe bloquear al camarero. */
  private markTableOccupied() {
    const tableId = this.selectedTable?.id;

    this.apiService.save(Urls.urls.tables, { status: 'occupied' }, tableId).subscribe({
      next: () => this.finishOrder(),
      error: () => this.finishOrder(),
    });
  }

  /** Best-effort: el ticket ya se ha guardado, así que un fallo al liberar la mesa no debe bloquear la impresión. */
  private releaseTableAndPrint(ticketId: number) {
    const tableId = this.selectedTable?.id;

    this.apiService.save(Urls.urls.tables, { status: 'free' }, tableId).subscribe({
      next: () => this.finishCheckout(ticketId),
      error: () => this.finishCheckout(ticketId),
    });
  }

  private finishCheckout(ticketId: number) {
    this.saving = false;
    window.open(`/ticket/print/${ticketId}`, '_blank');
    this.router.navigate(['/tickets']);
  }

  private finishOrder() {
    this.saving = false;
    this.router.navigate(['/tickets']);
  }

}
